<template>
<div :style="netStyle">
  <div class="box-head">Critical Residues</div>

  <div class="box-tool">
    <span><a style="cursor:pointer" @click="downloadTxt">{{task.id}}-net.txt</a> <a v-if="netReady" style="cursor:pointer" @click="downloadPng">{{task.id}}-net.png</a></span>
  </div>

  <div v-if="!showNet">
   <el-button type="text" @click.native="viewNet">View Network</el-button>
  </div>

  <div v-show="netReady">
    &nbsp;
    <span>Show 
      <select v-model="numPaths" @change="changeNumPaths">
        <option value="3">3</option>
        <option value="5">5</option>
        <option value="7">7</option>
        <option value="10">10</option>
        <option value="20">20</option>
        <option value="30">30</option>
        <option value="40">40</option>
        <option value="50">50</option>
        <option value="60">60</option>
        <option value="70">70</option>
        <option value="80">80</option>
        <option value="90">90</option>
        <option value="100">100</option>
        <option value="all">all</option>
      </select>
      pathways
    </span>
    &nbsp;
    Auto-update layout <el-switch v-model="layoutUpdate" active-color="#13ce66"></el-switch>
  </div>

  <div v-show="showNet" id="net" v-loading="showNet&&!netReady"></div>

  <div v-if="netReady" class="button-plus" @click="zoomIn"><i class="el-icon-plus"></i></div>
  <div v-if="netReady" class="button-minus" @click="zoomOut"><i class="el-icon-minus"></i></div>
</div>
</template>

<script>
import cytoscape from 'cytoscape'
import cola from 'cytoscape-cola'
import coseBilkent from 'cytoscape-cose-bilkent'
import klay from 'cytoscape-klay'
import interpolate from 'color-interpolate'
import { saveAs } from 'file-saver'

cytoscape.use(klay)

cytoscape.use(coseBilkent)
cytoscape.use(cola)

export default {
  name: 'Net',

  data () {
    return {
      showNet: false,
      netReady: false,
      numPaths: '5',
      active_site: [],
      allosteric_site: [],
      layout: {
        name: 'cola',
        fit: true
      },
      layoutUpdate: true,
      netStyle: {
        margin: '20px 0px',
        position: 'relative',
        '-webkit-user-select': 'none',
        '-moz-user-select': 'none',
        '-khtml-user-select': 'none',
        '-ms-user-select': 'none'
      }
    }
  },

  props: [ 'value' ],

  computed: {
    task () {
      return this.$store.state.task
    }
  },

  methods: {
    viewNet () {
      this.showNet = true
      this.$nextTick(() => {
        this.cy = this.initNet()

        this.cy.on('drag', e => {
          if (this.layoutUpdate) {
            this.cy.$layout.run()
          }
        })

        this.checkTask()
      })
    },

    changeNumPaths () {
      this.setElements()
      this.updateChart()
    },

    downloadPng () {
      let v = this
      saveAs(this.cy.png({'output': 'blob'}), `${v.task.id}-net.png`)
    },

    downloadTxt () {
      let v = this
      let nodes = [], edges = []
      if (v.elements) {
        nodes = v.elements.nodes
        edges = v.elements.edges
      } else if (v.task && v.task.results && v.task.results.paths) {
        let nodeMap = {}
        nodeMap.start = { id: 'start', name: 'Start', weight: 0 }
        nodeMap.end = { id: 'end', name: 'End', weight: 0 }
        let ind = v.task.results.ind || []
        for (let path of v.task.results.paths) {
          let weight = path.weight
          let ls = [nodeMap.start]
          for (let i of path.path) {
            if (!(i in nodeMap)) {
              let r = ind.find(j => j.num === parseInt(i))
              nodeMap[i] = { id: i, name: r ? r.name : '', weight: 0 }
            }
            ls.push(nodeMap[i])
          }
          ls.push(nodeMap.end)
          for (let el of ls) el.weight += weight
          for (let i = 0; i < ls.length - 1; i++) {
            edges.push({ data: { source: ls[i].id, target: ls[i + 1].id } })
          }
        }
        for (let id in nodeMap) {
          nodes.push({ data: { id: nodeMap[id].id, name: nodeMap[id].name, weight: nodeMap[id].weight } })
        }
      }
      let content = 'NODES\n' + nodes.map(n => `${n.data.id} ${n.data.name} ${n.data.weight}`).join('\n') + '\nEDGES\n' + edges.map(e => `${e.data.source} ${e.data.target}`).join('\n')
      var blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
      saveAs(blob, `${v.task.id}-net.txt`)
    },

    redirect (url) {
      window.location.href = url
    },

    openNewTab (url) {
      window.open(url, '_blank')
    },

    setElements () {
      let v = this
      let nodes = {}

      nodes.start = {
        weight: 0,
        id: 'start',
        name: 'Start',
        sons: new Set()
      }

      nodes.end = {
        weight: 0,
        id: 'end',
        name: 'End',
        sons: new Set()
      }

      let n = v.numPaths
      if (n === 'all') {
        n = 9999999
      }
      for (let path of v.task.results.paths.slice(0, n)) {
        let weight = path.weight

        let ls = [nodes.start]
        for (let i of path.path) {
          if (!(v.active_site.includes(parseInt(i))) && !(v.allosteric_site.includes(parseInt(i)))) {
            if (!(i in nodes)) {
              nodes[i] = {
                weight: 0,
                id: i,
                name: v.task.results.ind.find(j => { return j.num === parseInt(i) }).name,
                sons: new Set()
              }
            }
            ls.push(nodes[i])
          }
        }
        ls.push(nodes.end)

        for (let el of ls) {
          el.weight += weight
        }

        for (let i = 0; i < ls.length - 1; i++) {
          ls[i].sons.add(ls[i + 1].id)
        }
      }

      let max = 0.0
      for (let id in nodes) {
        let node = nodes[id]
        if (max < node.weight) max = node.weight
      }
      for (let id in nodes) {
        let node = nodes[id]
        node.weight /= max
      }

      let elements = {
        nodes: [], edges: []
      }
      for (let id in nodes) {
        let node = nodes[id]
        let obj = {
          data: { id: node.id, name: node.name, weight: node.weight }
        }
        if (id === 'start') {
          obj.position = {
            x: 0, y: 80
          }
        }
        if (id === 'end') {
          obj.position = {
            x: 350, y: 80
          }
        }
        elements.nodes.push(obj)
        for (let sonId of node.sons) {
          elements.edges.push({
            data: { id: `${id}-${sonId}`, source: id, target: sonId }
          })
        }
      }

//      if (v.numNodes !== 'all') {
//        elements.nodes.sort((a, b) => b.data.weight - a.data.weight)
//        elements.nodes = elements.nodes.slice(0, parseInt(v.numNodes))
//        console.log(JSON.stringify(elements.nodes))
//        elements.edges = elements.edges.filter(edge => { return elements.nodes.find(node => { return edge.data.source === node.data.id }) && elements.nodes.find(node => { return edge.data.target === node.data.id }) })
//      }

//      console.log(JSON.stringify(elements))
      v.elements = elements
      return elements
    },

    getInd (chainName, resNum) {
      for (let i of this.task.results.ind) {
//        console.log(JSON.stringify(i))
//        console.log(`${chainName}/${resNum}`)
        if (`${chainName}/${resNum}` === i.name) {
          return i.num
        }
      }
      return -1
//      return this.task.results.ind.find(i => i.name === `${chainName}/${resNum}`).num
    },

    getSites (sites) {
      let vec = []
      let ls = sites.trim().split(/,/)
      for (let s of ls) {
        let w = s.split(/\//)
        let chainName = w[0]
        let residues = w[1]
        let v = residues.split(/\+/)
        for (let i of v) {
          let u = i.split(/-/)
          if (u.length === 2) {
            let a = parseInt(u[0])
            let b = parseInt(u[1])
            for (let j = a; j <= b; j++) {
              vec.push(this.getInd(chainName, j))
            }
          } else if (u.length === 1) {
            vec.push(this.getInd(chainName, u[0]))
          }
        }
      }
      return vec
    },

    updateChart () {
//      this.cy.ready(e => {
//      console.log(this.elements)
      this.cy.json({
        elements: this.elements
      })
      let l = this.cy.layout(this.layout)
      this.cy.$layout = l
      l.run()
      this.cy.zoomingEnabled(true)
      this.netReady = true
//      })
    },

    checkTask () {
      let v = this
      let task = this.task

      if (task) {
        v.active_site = v.getSites(task.par.active_site)
        v.allosteric_site = v.getSites(task.par.allosteric_site)
        v.setElements()
        v.updateChart()
      } else {
        setTimeout(() => {
          v.checkTask()
        }, 500)
      }
    },

    zoomIn () {
      let v = this
      if (v.cy) {
        let zoomLevel = v.cy.zoom()
        v.cy.zoom(zoomLevel * 1.1)
      }
    },

    zoomOut () {
      let v = this
      if (v.cy) {
        let zoomLevel = v.cy.zoom()
        v.cy.zoom(zoomLevel * 0.9)
      }
    },

    initNet () {
      let v = this
      let colormap = interpolate(['#053795', 'green', 'yellow', '#900c3f'])
      return cytoscape({
        container: document.getElementById('net'),

        elements: {
          nodes: [
            { data: { id: 'start', name: 'Start', weight: 0.2 } },
            { data: { id: 'end', name: 'End', weight: 0.2 } }
          ],
          edges: [
            { data: { id: 'ab', source: 'start', target: 'end' } }
          ]
        },

        layout: v.layout,

        // so we can see the ids
        style: [{
          selector: 'node',
          style: {
            label: 'data(name)',
            width: function (el) { return el.data('weight') * 100 },
            height: function (el) { return el.data('weight') * 100 },
            'background-color': function (el) { return colormap(el.data('weight')) }
          }
        }]
      })
    }

  },

  mounted () {
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import "../styles/theme.scss";


#net {
  width: 100%;
  height: 450px;
  margin: 20px 0px;
  /*
  box-shadow: 0 0 5px #888888;
  */
}

.button-minus {
  position:absolute;
  right:10px;
  bottom:10px;
  width:40px;
  font-size:20pt;
  cursor: pointer;
}

.button-minus:hover {
  opacity: 0.5;
}

.button-minus .el-icon-minus {
  font-weight: 800;
}

.button-plus {
  position:absolute;
  right:70px;
  bottom:10px;
  font-size:20pt;
  cursor: pointer;
}

.button-plus .el-icon-plus {
  font-weight: 800;
}

.button-plus:hover {
  opacity: 0.5;
}

.box-head {
  padding: 10px;
  border-bottom: 1px solid #eeeeee;
  background-color: $theme-color;
  font-weight: 700;
  color: white;
}

.box-tool {
  padding:10px 0px;
}

</style>
